import { createAction } from "redux-actions";
import { takeLatest, put, select, call } from "redux-saga/effects";
import dayjs from "dayjs";
import { api } from "../api";
import { getCommonApiParams } from "../../../../utils/redux-helpers";
import { API_DATE_FORMAT } from "../../../../app_constants";

export const SET_LOADING = "saga/discounts/SET_LOADING";
export const SET_UPDATES_LOADING = "saga/discounts/SET_UPDATES_LOADING";
export const SET_FILTERS_LOADING = "saga/discounts/SET_FILTERS_LOADING";
export const SET_FILTERS_DIRTY = "saga/discounts/SET_FILTERS_DIRTY";
export const SET_DATES = "saga/discounts/SET_DATES";

export const FETCH_CURRENT_USER = "saga/discounts/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "saga/discounts/FETCH_CURRENT_USER_SUCCESS";
export const FETCH_FILTERS_SUCCESS = "saga/discounts/FETCH_FILTERS_SUCCESS";
export const FETCH_DATA_SUCCESS = "saga/discounts/FETCH_DATA_SUCCESS";

export const UPDATE_MODULE_VIEW = "saga/discounts/UPDATE_MODULE_VIEW";
export const UPDATE_FILTER = "saga/discounts/UPDATE_FILTER";
export const UPDATE_DATES = "saga/discounts/UPDATE_DATES";
export const UPDATE_CUSTOM_RANGE = "saga/discounts/UPDATE_CUSTOM_RANGE";
export const UPDATE_DATA = "saga/discounts/UPDATE_DATA";
export const UPDATE_TABLE_VIEW = "saga/discounts/UPDATE_TABLE_VIEW";
export const UPDATE_TABLE_VIEW_SELECTED = "saga/discounts/UPDATE_TABLE_VIEW_SELECTED";

export const RESET_FILTER = "saga/discounts/RESET_FILTER";
export const RESET_SELECTED_FILTERS = "saga/discounts/RESET_SELECTED_FILTERS";

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  updateModuleView: createAction(UPDATE_MODULE_VIEW),
  updateFilter: createAction(UPDATE_FILTER),
  updateDates: createAction(UPDATE_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  updateData: createAction(UPDATE_DATA),
  updateTableView: createAction(UPDATE_TABLE_VIEW),
  resetFilters: createAction(RESET_FILTER)
};

export default function* watcher() {
  yield takeLatest(FETCH_CURRENT_USER, fetchCurrentUserData);
  yield takeLatest(UPDATE_DATA, updateData);
  yield takeLatest(RESET_FILTER, resetFilters);
  yield takeLatest(UPDATE_DATES, fetchFilters);
  yield takeLatest(UPDATE_CUSTOM_RANGE, fetchFilters);
  yield takeLatest(UPDATE_MODULE_VIEW, updateView);
  yield takeLatest(UPDATE_TABLE_VIEW, updateTableView);
}

export function* fetchCurrentUserData(data) {
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: data.payload
  });

  yield put({ type: SET_LOADING, payload: true });

  yield setDates();

  try {
    yield fetchFilters();
    yield fetchData();

    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* updateView() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  yield setDates();

  try {
    yield fetchFilters();
    yield fetchData();

    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* updateTableView({ payload }) {
  yield put({ type: UPDATE_TABLE_VIEW_SELECTED, payload });
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield fetchData();

    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* fetchFilters() {
  const params = yield getFiltersApiParams();

  yield put({ type: SET_FILTERS_LOADING, payload: true });

  try {
    const { data } = yield call(api.getFilters, params);

    yield put({
      type: FETCH_FILTERS_SUCCESS,
      payload: data
    });
  } catch (error) {
    console.error(error);
  } finally {
    yield put({ type: SET_FILTERS_LOADING, payload: false });
  }
}

function* updateData() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield put({ type: SET_FILTERS_DIRTY, payload: false });

    yield fetchData();

    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* resetFilters() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  yield put({ type: RESET_SELECTED_FILTERS });
  yield setDates();

  try {
    yield fetchFilters();
    yield fetchData();

    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* setDates() {
  const endDate = yield select((state) => state.discounts.currentUser.calendarEndDate);
  const moduleView = yield select((state) => state.discounts.selectedFilters.moduleView);
  // Trend defaults to the last 12 months; Breakdown defaults to Month To Date.
  const startDate =
    moduleView.id === "trend"
      ? dayjs(endDate).subtract(11, "month").startOf("month").format(API_DATE_FORMAT)
      : dayjs(endDate).startOf("month").format(API_DATE_FORMAT);

  yield put({
    type: SET_DATES,
    payload: {
      startDate,
      endDate
    }
  });
}

function* getFiltersApiParams() {
  const commonParams = yield getCommonApiParams("commonReducer", "commonProps");
  const { startDate, endDate } = yield select((state) => state.discounts.selectedFilters);

  return {
    ...commonParams,
    start_date: startDate,
    end_date: endDate
  };
}

// Shared by both views — the single discounts_chart endpoint returns breakdown or trend data
// depending on this body (moduleView/table_view), so both fetches send identical params.
function* getApiParams() {
  const commonParams = yield getCommonApiParams("commonReducer", "commonProps");
  const { startDate, endDate, membershipTypes, staffs, moduleView, table_view } = yield select(
    (state) => state.discounts.selectedFilters
  );

  return {
    ...commonParams,
    start_date: startDate,
    end_date: endDate,
    membership_type_ids: membershipTypes.map((type) => type.id),
    staff_ids: staffs.map((staff) => staff.id),
    module_view: moduleView.id,
    table_view
  };
}

// One endpoint serves both views (module_view/table_view in the body pick the shape) — this
// is the single fetch every call site (mount, view switch, table toggle, Update, Reset) uses.
function* fetchData() {
  const params = yield getApiParams();

  try {
    const { data } = yield call(api.getValues, params);

    yield put({
      type: FETCH_DATA_SUCCESS,
      payload: data
    });
  } catch (error) {
    console.error(error);
  }
}
